import { useTranslation } from 'react-i18next';
import { taskStepOf } from '@d-orca/shared/client/activities';
import { mayRaiseIssueOn } from '@d-orca/shared/client/issues';
import { ActivityGroupStatus, type IIssue } from '@d-orca/shared/trusstor';
import { Pill } from '@components/ui/Pill/Pill';
import { TradePill } from '@components/ui/TradePill/TradePill';
import type { ISchedulerTask } from '@interfaces/IScheduler';
import { useAppDispatch } from '@store/hooks';
import { openCreateDialogForTask } from '@store/slices/dialog.slice';
import { CheckCircleIcon, HardHatIcon, LayersIcon, MessageSquareIcon, PersonIcon } from '@utils/icons';
import { tradeLabel } from '@utils/tradeLabel';
import { CardMenu, type ICardMenuItem } from '../../ActivitiesPage/CardList/CardMenu';
import { cardOpenHandlers } from '../../ActivitiesPage/CardList/cardOpenHandlers';
import { IssueMarks } from '../../ActivitiesPage/CardList/IssueMarks';
import { IssueStatusControl } from '../../ActivitiesPage/IssueStatus/IssueStatusControl';
import { formatIssueLocations } from '../../ActivitiesPage/model/issueDisplay';
import type { IListItem } from '../../ActivitiesPage/model/listItem';
import { FinishTaskDialog } from '../../SchedulerPage/components/FinishTaskDialog/FinishTaskDialog';
import { useSchedulerCapabilities } from '../../SchedulerPage/context/SchedulerCapabilities';
import { useSchedulerFrozenTasks, useSchedulerTask } from '../../SchedulerPage/hooks/useSchedulerStore';
import { useTaskCommitActions } from '../../SchedulerPage/hooks/useTaskCommitActions';
import { useTaskFinish } from '../../SchedulerPage/hooks/useTaskFinish';
import { useTaskLadderOrPlanned } from '../../SchedulerPage/hooks/useTaskLadderOrPlanned';
import { DueDay } from './DueDay';
import type { IItemTrade } from './layers';
import styles from './monitorCards.module.css';

interface ICardProps {
  item: IListItem;
  siteId: string;
  /** The site's zone, for the due tag. */
  tz: string;
  /** The item's trade; absent for an issue filed without one. */
  trade: IItemTrade | undefined;
  canManageIssues: boolean;
  onOpen: (item: IListItem) => void;
}

interface IDueTagProps {
  day: string | undefined;
  overdue: boolean;
  tz: string;
}

/** Trusstor's flag + date tag; red once the item is overdue. */
function DueTag({ day, overdue, tz }: IDueTagProps) {
  if (!day) return null;
  return (
    <Pill className={overdue ? `${styles.due} ${styles.dueOverdue}` : styles.due}>
      <DueDay day={day} tz={tz} />
    </Pill>
  );
}

interface ITradeLineProps {
  trade: IItemTrade;
}

/** The trade pill plus its contractor — Trusstor's trade line. */
function TradeLine({ trade }: ITradeLineProps) {
  const { t } = useTranslation();
  return (
    <span className={styles.trade}>
      <TradePill label={tradeLabel(t, trade.tradeId)} backgroundColor={trade.color ?? undefined} tradeId={trade.tradeId} />
      {trade.contractor && <span className={styles.contractor}>{trade.contractor}</span>}
    </span>
  );
}

interface IActivityMenuProps {
  item: IListItem;
  task: ISchedulerTask;
  siteId: string;
  canManageIssues: boolean;
  /** The schedule on screen — the menu's writes go to it. */
  scheduleNumber: string | undefined;
}

/**
 * An activity's ⋮ menu: Trusstor's per-status actions, each offered only where
 * D-ORCA already allows it — Raise issue (the card menu's rule), Mark done and
 * Take back commitment (the drawer step's rule). No menu when nothing applies.
 */
function ActivityMenu({ item, task, siteId, canManageIssues, scheduleNumber }: IActivityMenuProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const storeTask = useSchedulerTask(siteId, task.id);
  const ladder = useTaskLadderOrPlanned(storeTask, task.id);
  const { canExecute } = useSchedulerCapabilities();
  const { uncommit, pending } = useTaskCommitActions({ siteId, taskId: task.id, scheduleNumber });
  const finish = useTaskFinish({ siteId, taskId: task.id, scheduleNumber });
  const frozen = useSchedulerFrozenTasks(siteId);

  // Trusstor's statuses: `overdue` has started and run late, `delayed` has not started on time.
  const status = item.activityStatus;
  const started = status === ActivityGroupStatus.inProgress || status === ActivityGroupStatus.overdue;
  const notStarted = status === ActivityGroupStatus.planned || status === ActivityGroupStatus.delayed;
  const items: ICardMenuItem[] = [];
  if (status !== ActivityGroupStatus.complete && canManageIssues && mayRaiseIssueOn(task, status)) {
    items.push({
      label: t('activities.card.raiseIssue'),
      onSelect: () => dispatch(openCreateDialogForTask({ id: task.id, title: item.title })),
    });
  }
  if (canExecute && storeTask) {
    // A committed activity is a leaf — the feed never carries a parent — so its
    // step needs no walk of the plan for leaves.
    const step = taskStepOf(storeTask, ladder.rung);
    if (step.kind === 'markDone' && started) {
      items.push({
        label: t('scheduler.panel.actions.markDone'),
        disabled: step.remaining > 0,
        onSelect: () => finish.requestDone(storeTask),
      });
    }
    if (step.kind === 'markDone' && notStarted && step.canTakeBack) {
      items.push({ label: t('scheduler.commit.uncommitAction'), disabled: pending, onSelect: () => void uncommit() });
    }
  }

  const { question } = finish;
  return (
    <>
      {items.length > 0 && <CardMenu label={t('activities.card.activityActions')} items={items} />}
      {question && (
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <FinishTaskDialog
            question={question}
            frozen={frozen}
            pending={finish.pending}
            onConfirm={(spans) => void finish.confirm(question, spans)}
            onClose={finish.dismiss}
          />
        </span>
      )}
    </>
  );
}

interface IActivityCardProps extends ICardProps {
  /** The committed task behind the item. */
  task: ISchedulerTask | undefined;
  /** The schedule on screen — the menu's writes go to it. */
  scheduleNumber: string | undefined;
}

/**
 * An activity in the Monitor list — Trusstor's widget activity card: the name
 * over its flagged end date and ⋮ menu, then the trade with its contractor and
 * how many of its areas are complete.
 */
export function MonitorActivityCard({
  item,
  task,
  siteId,
  tz,
  trade,
  canManageIssues,
  onOpen,
  scheduleNumber,
}: IActivityCardProps) {
  const areas = task?.locations ?? [];
  const done = areas.filter((area) => area.status === 'complete').length;
  return (
    <div className={styles.card} {...cardOpenHandlers(item.title, () => onOpen(item))}>
      <span className={styles.head}>
        <span className={styles.activityTitle}>{item.title}</span>
        <DueTag day={item.end} overdue={item.isOverdue} tz={tz} />
        {task && (
          <ActivityMenu
            item={item}
            task={task}
            siteId={siteId}
            canManageIssues={canManageIssues}
            scheduleNumber={scheduleNumber}
          />
        )}
      </span>
      {(trade || areas.length > 0) && (
        <span className={styles.foot}>
          {trade && <TradeLine trade={trade} />}
          {areas.length > 0 && (
            <span className={styles.areasDone}>
              <CheckCircleIcon size={16} />
              {done}/{areas.length}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

interface IIssueCardProps extends ICardProps {
  issue: IIssue;
}

/**
 * An issue in the Monitor list — Trusstor's issue item: the critical / safety
 * marks over the title, then location, trade, assignee and comment count, and
 * the issue's next status move (Complete, Review…) as the A&I card offers it.
 */
export function MonitorIssueCard({ item, issue, siteId, tz, trade, canManageIssues, onOpen }: IIssueCardProps) {
  const { t } = useTranslation();
  const locations = issue.locations ?? [];
  return (
    <div className={styles.card} {...cardOpenHandlers(item.title, () => onOpen(item))}>
      <span className={styles.head}>
        <IssueMarks issue={issue} canManage={canManageIssues} siteId={siteId} />
        <span className={styles.spacer} />
        <DueTag day={item.end} overdue={item.isOverdue} tz={tz} />
      </span>
      <span className={styles.issueTitle}>{item.title}</span>
      <span className={styles.details}>
        {locations.length > 0 && (
          <span className={styles.detail}>
            <LayersIcon size={16} />
            {formatIssueLocations(locations)}
          </span>
        )}
        {trade && (
          <span className={styles.detail}>
            <HardHatIcon size={16} />
            <TradeLine trade={trade} />
          </span>
        )}
        {item.assigneeName && (
          <span className={styles.detail}>
            <PersonIcon size={16} />
            {item.assigneeName}
          </span>
        )}
        {item.commentCount > 0 && (
          <span className={styles.detail}>
            <MessageSquareIcon size={16} />
            {t('monitor.layers.comments', { count: item.commentCount })}
          </span>
        )}
      </span>
      <IssueStatusControl issue={issue} siteId={siteId} canManage={canManageIssues} className={styles.statusAction} />
    </div>
  );
}
